import androidx.compose.runtime.*
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date
import kotlin.math.roundToInt

private val EVENT_KINDS = listOf("tournament" to "Turnaj", "announcement" to "Vyhlášení", "meeting" to "Schůze", "other" to "Jiné")
private const val ROUND_SLOT_DAYS = 14

private fun eventKindLabel(kind: String): String = EVENT_KINDS.find { it.first == kind }?.second ?: kind

private fun addDaysIso(iso: String, days: Int): String {
    val d = Date(isoEpochDay(iso) * 86_400_000.0 + days.toDouble() * 86_400_000.0)
    val y = d.getUTCFullYear()
    val m = (d.getUTCMonth() + 1).toString().padStart(2, '0')
    val day = d.getUTCDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

private fun daysBetweenIso(a: String, b: String): Int =
    if (a.isBlank() || b.isBlank()) 0 else (isoEpochDay(b) - isoEpochDay(a)).roundToInt()

/**
 * Carries every later round along with an earlier one's edit, in either direction, while keeping
 * each round's own length AND its original gap-to-predecessor (so a deliberately larger gap, e.g.
 * a holiday break between two specific rounds, stays that size instead of collapsing to zero).
 * Gaps are read from `original` (the state right before this edit) and re-applied starting from
 * the edited anchor's new position, chained forward round by round — not just re-closing overlaps,
 * so moving a round earlier pulls the rest of the schedule earlier too, not only later.
 */
private fun cascadeForward(
    original: Map<String, SeasonRoundSlotDto>,
    editedAnchor: SeasonRoundSlotDto,
): Map<String, SeasonRoundSlotDto> {
    val orderedOriginal = original.values.sortedBy { it.roundNumber }
    val updated = original.toMutableMap()
    updated[editedAnchor.id] = editedAnchor

    var prevOriginal = original[editedAnchor.id] ?: editedAnchor
    var prevNew = editedAnchor
    for (round in orderedOriginal) {
        if (round.roundNumber <= editedAnchor.roundNumber) continue
        val prevOriginalEnd = prevOriginal.dateTo ?: break
        val newPrevEnd = prevNew.dateTo ?: break
        val gapDays = daysBetweenIso(prevOriginalEnd, round.dateFrom)
        val newDateFrom = addDaysIso(newPrevEnd, gapDays)
        val duration = round.dateTo?.let { daysBetweenIso(round.dateFrom, it) }
        val shifted = round.copy(dateFrom = newDateFrom, dateTo = duration?.let { addDaysIso(newDateFrom, it) })
        updated[round.id] = shifted
        prevOriginal = round
        prevNew = shifted
    }
    return updated
}

/** Season-prep step (spec 11): shared round-date windows for every league in the season, plus freeform events. */
@Composable
fun TerminovaListinaScreen(token: String?, role: String?, seasonId: String, onOpenPrint: () -> Unit) {
    var slots by remember(seasonId) { mutableStateOf<List<SeasonRoundSlotDto>>(emptyList()) }
    var events by remember(seasonId) { mutableStateOf<List<SeasonEventDto>>(emptyList()) }
    var leagues by remember(seasonId) { mutableStateOf<List<SeasonLeagueDto>>(emptyList()) }
    var loading by remember(seasonId) { mutableStateOf(true) }
    var error by remember(seasonId) { mutableStateOf<String?>(null) }
    var refreshKey by remember(seasonId) { mutableStateOf(0) }
    var totalRounds by remember(seasonId) { mutableStateOf("14") }
    var extraLeagueIds by remember(seasonId) { mutableStateOf<Set<String>>(emptySet()) }
    var extraAfterRound by remember(seasonId) { mutableStateOf("") }
    var extraBusy by remember(seasonId) { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // Working copy the admin edits; reset to the server's version whenever it's (re)loaded, so a
    // batch save (or a fresh generation) starts the next round of edits from a clean baseline.
    var editedSlots by remember(seasonId) { mutableStateOf<Map<String, SeasonRoundSlotDto>>(emptyMap()) }

    LaunchedEffect(seasonId, refreshKey) {
        loading = true
        try {
            slots = Api.fetchRoundSlots(token, seasonId)
            editedSlots = slots.associateBy { it.id }
            events = Api.fetchSeasonEvents(token, seasonId)
            leagues = Api.fetchLeagues(token, seasonId)
            error = null
        } catch (e: Throwable) {
            error = "Načtení termínové listiny selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val originalById = slots.associateBy { it.id }
    val dirtyIds = editedSlots.filterValues { edited ->
        val orig = originalById[edited.id]
        orig != null && (orig.dateFrom != edited.dateFrom || orig.dateTo != edited.dateTo)
    }.keys

    fun onDateFromChange(rowId: String, newValue: String) {
        val current = editedSlots[rowId] ?: return
        if (newValue.isBlank()) return
        val editedAnchor = current.copy(dateFrom = newValue, dateTo = addDaysIso(newValue, ROUND_SLOT_DAYS - 1))
        editedSlots = cascadeForward(editedSlots, editedAnchor)
    }

    fun onDateToChange(rowId: String, newValue: String) {
        val current = editedSlots[rowId] ?: return
        val editedAnchor = current.copy(dateTo = newValue.ifBlank { null })
        editedSlots = cascadeForward(editedSlots, editedAnchor)
    }

    var saving by remember(seasonId) { mutableStateOf(false) }

    Div(attrs = { classes("card") }) {
        H2 { Text("Termínová listina") }
        Div(attrs = { classes("actions") }) {
            Button(attrs = { onClick { onOpenPrint() } }) { Text("Tisk rozlosování") }
        }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }
    }

    Div(attrs = { classes("card") }) {
        H3 { Text("Kola") }
        P { Text("Termínová okna kol (typicky 14 dní) sdílená pro všechny ligy sezóny — jen číslo kola a datum, bez rozlišení podzim/jaro/nadstavba. To, které kolo je pro danou ligu podzimní, jarní nebo nadstavbové, se určí až při generování rozpisu té konkrétní ligy (různé ligy sezóny mohou mít různý počet kol i jiný význam stejného čísla kola).") }
        if (role.canManage()) {
            Div(attrs = { classes("actions") }) {
                Label { Text("Počet kol v sezóně: ") }
                Input(type = InputType.Text) {
                    value(totalRounds)
                    onInput { totalRounds = it.value.filter { c -> c.isDigit() } }
                }
                Button(attrs = {
                    if (totalRounds.toIntOrNull() == null || totalRounds.toInt() < 1) disabled()
                    onClick {
                        scope.launch {
                            try {
                                Api.generateRoundSlots(token, seasonId, totalRounds.toInt())
                                refreshKey++
                            } catch (e: Throwable) {
                                error = "Generování kol selhalo: ${e.apiMessage()}"
                            }
                        }
                    }
                }) { Text("Vygenerovat kola") }
            }
            if (slots.any { it.leagueIds.isNotEmpty() }) {
                P { Text("Pozor: „Vygenerovat kola“ smaže i kola navíc přidaná níže pro jednotlivé ligy.") }
            }
        }
        if (role.canManage() && leagues.isNotEmpty()) {
            P {
                Text("Kolo navíc platí jen pro zaškrtnuté ligy — ostatní ligy sezóny mají ten týden volno (např. větší liga s víc týmy potřebuje víc kol než zbytek sezóny). Vkládejte kola navíc dřív, než vygenerujete rozpisy jednotlivých lig; pokud je vložíte později, je nutné rozpisy dotčených lig přegenerovat.")
            }
            Div(attrs = { classes("actions") }) {
                leagues.forEach { l ->
                    Label {
                        CheckboxInput(checked = l.id in extraLeagueIds) {
                            onInput { extraLeagueIds = if (it.value) extraLeagueIds + l.id else extraLeagueIds - l.id }
                        }
                        Text(" ${l.name}")
                    }
                }
                Label { Text(" po kole číslo: ") }
                Input(type = InputType.Text) {
                    value(extraAfterRound)
                    onInput { extraAfterRound = it.value.filter { c -> c.isDigit() } }
                }
                Button(attrs = {
                    if (extraBusy || extraLeagueIds.isEmpty() || extraAfterRound.toIntOrNull() == null) disabled()
                    onClick {
                        extraBusy = true
                        error = null
                        scope.launch {
                            try {
                                Api.insertExtraRoundSlot(token, seasonId, extraAfterRound.toInt(), extraLeagueIds.toList())
                                extraAfterRound = ""
                                extraLeagueIds = emptySet()
                                refreshKey++
                            } catch (e: Throwable) {
                                error = "Vložení kola navíc selhalo: ${e.apiMessage()}"
                            }
                            extraBusy = false
                        }
                    }
                }) { Text(if (extraBusy) "Vkládám…" else "Vložit kolo navíc") }
            }
        }
        if (editedSlots.isEmpty()) {
            if (!loading) Text("Zatím žádná vygenerovaná kola.")
        } else {
            if (role.canManage()) {
                Div(attrs = { classes("actions") }) {
                    Button(attrs = {
                        if (saving || dirtyIds.isEmpty()) disabled()
                        onClick {
                            saving = true
                            error = null
                            scope.launch {
                                try {
                                    coroutineScope {
                                        dirtyIds.map { id ->
                                            val s = editedSlots.getValue(id)
                                            async { Api.updateRoundSlot(token, id, s.dateFrom, s.dateTo) }
                                        }.forEach { it.await() }
                                    }
                                    refreshKey++
                                } catch (e: Throwable) {
                                    error = "Uložení selhalo: ${e.apiMessage()}"
                                }
                                saving = false
                            }
                        }
                    }) { Text(if (saving) "Ukládám…" else "Uložit změny (${dirtyIds.size})") }
                }
            }
            Table {
                Tr {
                    Th { Text("Kolo") }
                    Th { Text("Od") }
                    Th { Text("Do") }
                    Th { Text("Liga") }
                    if (role.canManage()) Th { }
                }
                editedSlots.values.sortedBy { it.roundNumber }.forEach { slot ->
                    key(slot.id) {
                        Tr(attrs = { if (slot.id in dirtyIds) classes("dirty-row") }) {
                            Td { Text("Kolo ${slot.roundNumber}") }
                            Td {
                                Input(type = InputType.Date) {
                                    value(slot.dateFrom)
                                    if (!role.canManage()) disabled()
                                    onInput { onDateFromChange(slot.id, it.value) }
                                }
                            }
                            Td {
                                Input(type = InputType.Date) {
                                    value(slot.dateTo ?: "")
                                    if (!role.canManage()) disabled()
                                    onInput { onDateToChange(slot.id, it.value) }
                                }
                            }
                            Td {
                                if (slot.leagueIds.isNotEmpty()) {
                                    val names = slot.leagueIds.map { lid -> leagues.find { it.id == lid }?.name ?: "?" }
                                    Text("jen ${names.joinToString(", ")}")
                                }
                            }
                            if (role.canManage()) {
                                Td {
                                    if (slot.leagueIds.isNotEmpty()) {
                                        Button(attrs = {
                                            onClick {
                                                scope.launch {
                                                    try {
                                                        Api.deleteExtraRoundSlot(token, slot.id)
                                                        refreshKey++
                                                    } catch (e: Throwable) {
                                                        error = "Smazání selhalo: ${e.apiMessage()}"
                                                    }
                                                }
                                            }
                                        }) { Text("Smazat") }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    SeasonEventsCard(token = token, role = role, seasonId = seasonId, events = events, onChanged = { refreshKey++ })
}

@Composable
private fun SeasonEventsCard(
    token: String?,
    role: String?,
    seasonId: String,
    events: List<SeasonEventDto>,
    onChanged: () -> Unit,
) {
    var editing by remember(seasonId) { mutableStateOf<SeasonEventDto?>(null) }
    var error by remember(seasonId) { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val target = editing
    if (target != null) {
        SeasonEventForm(
            token = token,
            seasonId = seasonId,
            event = target,
            onCancel = { editing = null },
            onSaved = { editing = null; onChanged() },
        )
        return
    }

    Div(attrs = { classes("card") }) {
        H3 { Text("Ostatní události") }
        P { Text("Turnaje, vyhlášení, schůze apod. — bez vlivu na průběh ligy.") }
        if (role.canManage()) {
            Div(attrs = { classes("actions") }) {
                Button(attrs = {
                    onClick {
                        editing = SeasonEventDto(id = "", seasonId = seasonId, title = "", kind = "other", dateFrom = "", dateTo = null, note = null, linkUrl = null, tournamentId = null)
                    }
                }) { Text("Nová událost") }
            }
        }
        error?.let { Div { Text("Chyba: $it") } }

        if (events.isEmpty()) {
            Text("Zatím žádné události.")
        } else {
            Table {
                Tr {
                    Th { Text("Titulek") }
                    Th { Text("Druh") }
                    Th { Text("Od") }
                    Th { Text("Do") }
                    Th { Text("Odkaz") }
                    Th { }
                }
                events.forEach { ev ->
                    Tr {
                        Td { Text(ev.title) }
                        Td { Text(eventKindLabel(ev.kind)) }
                        Td { Text(formatDate(ev.dateFrom)) }
                        Td { Text(ev.dateTo?.let { formatDate(it) } ?: "") }
                        Td { Text(if (ev.linkUrl != null || ev.tournamentId != null) "ano" else "—") }
                        Td {
                            if (role.canManage()) {
                                Button(attrs = { onClick { editing = ev } }) { Text("Upravit") }
                                Button(attrs = {
                                    onClick {
                                        scope.launch {
                                            try {
                                                Api.deleteSeasonEvent(token, ev.id)
                                                onChanged()
                                            } catch (e: Throwable) {
                                                error = "Smazání selhalo: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }) { Text("Smazat") }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SeasonEventForm(
    token: String?,
    seasonId: String,
    event: SeasonEventDto,
    onCancel: () -> Unit,
    onSaved: () -> Unit,
) {
    var title by remember { mutableStateOf(event.title) }
    var kind by remember { mutableStateOf(event.kind) }
    var dateFrom by remember { mutableStateOf(event.dateFrom) }
    var dateTo by remember { mutableStateOf(event.dateTo ?: "") }
    var note by remember { mutableStateOf(event.note ?: "") }
    var linkUrl by remember { mutableStateOf(event.linkUrl ?: "") }
    var tournamentId by remember { mutableStateOf(event.tournamentId ?: "") }
    var pages by remember { mutableStateOf<List<ContentPageDto>>(emptyList()) }
    var tournaments by remember { mutableStateOf<List<TournamentDto>>(emptyList()) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val isNew = event.id.isBlank()

    LaunchedEffect(Unit) {
        pages = try { Api.fetchContentPages(token) } catch (e: Throwable) { emptyList() }
        tournaments = try { Api.fetchTournaments(token, seasonId) } catch (e: Throwable) { emptyList() }
    }

    Div(attrs = { classes("card") }) {
        H3 { Text(if (isNew) "Nová událost" else "Úprava události") }
        Div {
            Label { Text("Titulek: ") }
            Input(type = InputType.Text) { value(title); onInput { title = it.value } }
        }
        Div {
            Label { Text("Druh: ") }
            Select(attrs = { onChange { kind = it.value ?: "other" } }) {
                EVENT_KINDS.forEach { (value, label) ->
                    Option(value, attrs = { if (value == kind) selected() }) { Text(label) }
                }
            }
        }
        Div {
            Label { Text("Od: ") }
            Input(type = InputType.Date) {
                value(dateFrom)
                onInput {
                    val wasSameAsFrom = dateTo.isBlank() || dateTo == dateFrom
                    dateFrom = it.value
                    if (wasSameAsFrom) dateTo = it.value
                }
            }
            Label { Text(" Do: ") }
            Input(type = InputType.Date) { value(dateTo); onInput { dateTo = it.value } }
        }
        Div {
            Label { Text("Odkaz (URL, např. na stránku z Obsah webu): ") }
            Input(type = InputType.Text) {
                placeholder("https://… nebo /stranka/nazev")
                value(linkUrl)
                onInput { linkUrl = it.value }
            }
            if (pages.isNotEmpty()) {
                Select(attrs = {
                    onChange { event -> event.value?.takeIf { it.isNotBlank() }?.let { slug -> linkUrl = "/stranka/$slug" } }
                }) {
                    Option("", attrs = { selected() }) { Text("— vybrat stránku z Obsah webu —") }
                    pages.forEach { p -> Option(p.slug) { Text(p.title) } }
                }
            }
        }
        // Vazba na turnaj (spec 17.3): událost se pak na veřejné termínové listině vykreslí jako
        // odkaz na popisnou stránku turnaje, i kdyby se jeho slug později změnil.
        if (tournaments.isNotEmpty()) {
            Div {
                Label { Text("Turnaj: ") }
                Select(attrs = { onChange { ev -> tournamentId = ev.value ?: "" } }) {
                    Option("", attrs = { if (tournamentId.isBlank()) selected() }) { Text("— bez vazby na turnaj —") }
                    tournaments.forEach { t ->
                        Option(t.id, attrs = { if (t.id == tournamentId) selected() }) { Text(t.title) }
                    }
                }
            }
        }
        Div {
            Label { Text("Poznámka:") }
            TextArea(value = note, attrs = {
                classes("wide-textarea")
                attr("rows", "6")
                onInput { note = it.value }
            })
        }
        if (linkUrl.isBlank()) {
            Text("Bez odkazu se poznámka na veřejné stránce zobrazí při najetí myší nebo kliknutí na událost.")
        }
        Div(attrs = { classes("save-action") }) {
            Button(attrs = {
                if (saving || title.isBlank() || dateFrom.isBlank()) disabled()
                onClick {
                    saving = true
                    error = null
                    scope.launch {
                        try {
                            val input = SeasonEventInput(
                                seasonId, title.trim(), kind, dateFrom, dateTo.ifBlank { null },
                                note.ifBlank { null }, linkUrl.ifBlank { null },
                                tournamentId.ifBlank { null },
                            )
                            if (isNew) Api.createSeasonEvent(token, input) else Api.updateSeasonEvent(token, event.id, input)
                            onSaved()
                        } catch (e: Throwable) {
                            error = "Uložení selhalo: ${e.apiMessage()}"
                            saving = false
                        }
                    }
                }
            }) { Text(if (saving) "Ukládám…" else "Uložit") }
            Button(attrs = { onClick { onCancel() } }) { Text("Zpět") }
        }
        error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
    }
}
